import { useEffect } from "react";
import { motion } from "framer-motion";
import { Gift, Cake, Flower, ForkKnife, Moon, HandHeart, ArrowDown } from "@phosphor-icons/react";
import { SpecialBooking } from "../components/SpecialBooking";
import { SPECIAL_SLOGAN, SPECIAL_ITEMS, PHONE, PHONE_RAW } from "../data";

const HERO_IMG =
  "https://images.unsplash.com/photo-1608825154649-2e9bb4cd4211?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const ICONS = [Gift, Cake, Flower, ForkKnife];

export default function SpecialArrangements() {
  useEffect(() => { window.scrollTo({ top: 0 }); }, []);

  return (
    <main data-testid="special-page" className="bg-paper">
      {/* Hero */}
      <section className="relative min-h-[78vh] flex items-end overflow-hidden bg-ink">
        <img src={HERO_IMG} alt="Gifts and flowers" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 pt-32 w-full">
          <motion.span
            initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-brandred text-white text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 mb-6"
          >
            <Moon weight="fill" size={14} /> Special Arrangements
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 25 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-display font-extrabold text-white uppercase leading-[0.9] tracking-tighter text-4xl sm:text-6xl lg:text-7xl max-w-4xl"
          >
            {SPECIAL_SLOGAN}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="mt-6 max-w-2xl text-white/85 text-base sm:text-lg leading-relaxed"
          >
            Make someone's day unforgettable. Gifts, birthday cakes, fresh flowers and food —
            booked with us and delivered right on midnight. We knock, you smile.
          </motion.p>
          <motion.button
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
            data-testid="special-hero-book"
            onClick={() => document.querySelector("#book")?.scrollIntoView({ behavior: "smooth" })}
            className="mt-8 inline-flex items-center gap-2.5 bg-sun text-ink font-bold text-base px-7 py-4 border-2 border-sun shadow-hard hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-all duration-150"
          >
            Book a surprise <ArrowDown weight="bold" size={18} />
          </motion.button>
        </div>
      </section>

      {/* Midnight promise */}
      <section className="bg-ink text-white py-8 border-y-2 border-sun">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-wrap items-center gap-x-10 gap-y-3">
          <span className="font-display font-extrabold text-2xl text-sun">12:00 AM</span>
          <span className="flex items-center gap-2 text-white/80"><HandHeart weight="fill" size={20} className="text-sun" /> Right at midnight, at your door</span>
          <span className="text-white/60">Perfect for surprises, proposals &amp; last-minute celebrations</span>
        </div>
      </section>

      {/* Offerings */}
      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brandred mb-3">What we arrange</p>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tighter leading-[0.95] text-ink max-w-2xl">
            One booking. A whole lot of <span className="text-brandblue">happiness.</span>
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {SPECIAL_ITEMS.map((item, i) => {
              const Icon = ICONS[i];
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  data-testid={`special-item-${i}`}
                  className="bg-white border-2 border-ink p-6 hover:shadow-hard transition-shadow duration-200"
                >
                  <div className="w-14 h-14 bg-sun border-2 border-ink flex items-center justify-center">
                    <Icon weight="fill" size={28} className="text-ink" />
                  </div>
                  <h3 className="font-display font-extrabold text-2xl tracking-tight mt-5 text-ink">{item.title}</h3>
                  <p className="mt-2 text-ink/60 leading-relaxed text-sm">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-4 bg-sun border-2 border-ink p-6 shadow-hard-sm">
            <p className="font-display font-bold text-xl text-ink flex-1 min-w-[220px]">
              Have something specific in mind? Just tell us — we'll make it happen.
            </p>
            <a
              data-testid="special-call-btn"
              href={`tel:${PHONE_RAW}`}
              className="bg-ink text-sun font-bold px-6 py-3 border-2 border-ink hover:bg-white hover:text-ink transition-colors"
            >
              Call {PHONE}
            </a>
          </div>
        </div>
      </section>

      <SpecialBooking defaultService="Special Arrangement (Gifts, Flowers, Cake)" />
    </main>
  );
}
